import CodingEntityDropdown from 'app/pages/overview/tabs/transactions/CodingEntityDropdown';
import { useMemo } from 'react';
import { AgaveCostType, AgaveProject } from 'API';
import { useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';

// Type guard function to verify AgaveCostType structure
const isAgaveCostType = (value: unknown): value is AgaveCostType => {
  return (
    typeof value === 'object' && value !== null && 'id' in value && 'name' in value && 'source_id' in value
    // Note: code and project_id are optional in the AgaveCostType interface
  );
};

interface AgaveCostTypesDropdownProps {
  disabled?: boolean;
  disableHeader?: boolean;
  selectedCostType?: AgaveCostType | null;
  selectedProject?: AgaveProject | null;
  selectedCostCode?: { id: string } | null;
  onValueChange: (option: AgaveCostType | null) => void;
  placeholder?: string | null;
  enabled?: boolean;
  /** Load options only when dropdown opens (use in table rows to avoid stale shared state). */
  fetchOnlyOnOpen?: boolean;
}

const AgaveCostTypesDropdown = ({
  disabled,
  disableHeader = true,
  selectedCostType,
  selectedProject,
  selectedCostCode,
  onValueChange,
  placeholder,
  enabled = true,
  fetchOnlyOnOpen,
}: AgaveCostTypesDropdownProps) => {
  const { selectedPaidol } = useAppSelector(selectUserCompanies);

  // When the setting is enabled, the field is inactive until a project is selected
  const requireProjectFirst = Boolean(
    selectedPaidol?.requireProjectBeforeCostCode?.isEnabled && !selectedProject
  );

  // Type-safe wrapper for onValueChange
  const handleValueChange = useMemo(
    () => (option: AgaveCostType | null) => {
      if (!option) {
        onValueChange(null);
        return;
      }

      // Runtime type check to ensure the option is of the expected type
      if (!isAgaveCostType(option)) {
        // eslint-disable-next-line no-console
        console.warn('AgaveCostTypesDropdown: Invalid cost type selected:', option);
        return;
      }

      onValueChange(option);
    },
    [onValueChange]
  );

  return (
    <CodingEntityDropdown<AgaveCostType>
      entityType="costTypes"
      selectedItem={selectedCostType}
      onValueChange={handleValueChange}
      disabled={disabled || requireProjectFirst}
      disableHeader={disableHeader}
      placeholder={placeholder || undefined}
      identifierField="code"
      projectId={selectedProject?.id}
      costCodeId={selectedCostCode?.id}
      enabled={enabled}
      dataDropdownType="agave-cost-types"
      fetchOnlyOnOpen={fetchOnlyOnOpen}
    />
  );
};

export default AgaveCostTypesDropdown;
